/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import Chart from "react-apexcharts";
import dayjs from "dayjs";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useDropStats } from "../../store/useDropStats";
import { useDrops } from "../../store/useDrops";
import { get } from "http";
import { useDashboardStats } from "../../store/useDashboardStats";

interface Props {
  periodProp?: "Daily" | "Weekly" | "LastTwelweMonths" | "Monthly" | "Yearly";
  monthRangeProp?: Date;
}

export const ActiveUsersBasedOnDrops = React.memo(
  ({ periodProp, monthRangeProp }: Props) => {
    /** Refs */
    const snapshotRef = useRef(null);
    /** Hooks */
    const { activeUsersBasedOnDrops, getActiveUsersBasedOnDrops } =
      useDropStats();
    const [_, takeScreenshot] = useScreenshot();
    const { dashboardStats, getStaticStats } = useDashboardStats();
    /** State */
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const [periodYear, setPeriodYear] = useState<number>(currentYear);

    /** Callbacks */
    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        // Create a link to download the image
        const link = document.createElement("a");
        link.href = image;
        link.download = "active_users_based_on_drops.png"; // Set the default file name
        link.click(); // Programmatically click the link to trigger download
      });
    };

    /** Effects */
    useEffect(() => {
      if (monthRangeProp) setPeriodYear(monthRangeProp.getFullYear());
    }, [periodProp, monthRangeProp]);

    useEffect(() => {
      getStaticStats("AllTime");
    }, []);

    useEffect(() => {
      const requestedDay = new Date(
        `${currentDate.getMonth() + 1}-${
          currentDate.getDay() + 1
        }-${periodYear}`
      );
      getActiveUsersBasedOnDrops(requestedDay);
    }, [periodYear]);

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12">
            Drops per user per month
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end">
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {periodYear}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  onClick={() => setPeriodYear(currentYear)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {currentYear}
                </MenuItem>
                <MenuItem
                  onClick={() => setPeriodYear(currentYear - 1)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {currentYear - 1}
                </MenuItem>
                <MenuItem
                  onClick={() => setPeriodYear(currentYear - 2)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {currentYear - 2}
                </MenuItem>
                <MenuItem
                  onClick={() => setPeriodYear(currentYear - 3)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {currentYear - 3}
                </MenuItem>
              </MenuList>
            </Menu>
            <div className="ml-0 sm:ml-2" />
            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
            />
          </div>
        </div>
        <div ref={snapshotRef}>
          {/* @ts-ignore */}
          {Object.values(activeUsersBasedOnDrops?.usersWithoutADrop ?? {})
            ?.length ||
          Object.values(activeUsersBasedOnDrops?.usersWithOneDrop ?? {})
            ?.length ||
          Object.values(
            activeUsersBasedOnDrops?.usersWithMoreThanThreeDrops ?? {}
          )?.length ? (
            // @ts-ignore
            <Chart
              {...chartConfig}
              // @ts-ignore
              options={{
                ...chartConfig?.options,
                xaxis: {
                  ...chartConfig?.options?.xaxis,
                  categories: Object.keys(
                    activeUsersBasedOnDrops?.usersWithOneDrop ?? {}
                  ),
                  labels: {
                    ...chartConfig?.options?.xaxis?.labels,
                    // @ts-ignore
                    formatter: function (value: number) {
                      return dayjs()
                        .add(value - 1, "month")
                        .format("MMM");
                    },
                  },
                },
                tooltip: {
                  theme: "dark",
                  y: {
                    formatter: (value: number) => {
                      const totalUsers = dashboardStats?.totalUsers ?? 0;
                      const percentage = (value / totalUsers) * 100;
                      return `${percentage.toFixed(2)}%`;
                    },
                  },
                  // number of drops per user per month
                },
                // options?.xaxis?.categories = []
              }}
              series={[
                {
                  name: "1 drop",
                  data: Object.values(
                    activeUsersBasedOnDrops?.usersWithOneDrop ?? {}
                  ),
                },
                {
                  name: "2 drops",
                  data: Object.values(
                    activeUsersBasedOnDrops?.usersWithTwoDrops ?? {}
                  ),
                },
                {
                  name: "3+ drops",
                  data: Object.values(
                    activeUsersBasedOnDrops?.usersWithMoreThanThreeDrops ?? {}
                  ),
                },
              ]}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
                No data found
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

const chartConfig = {
  type: "bar",
  width: "100%",
  height: 240,
  options: {
    chart: {
      toolbar: {
        show: false,
      },
    },
    title: {
      show: "",
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: "12px",
        fontFamily: "Satoshi",
        fontWeight: 500,
        colors: ["#64748B"],
      },
      formatter: function (val: any) {
        return val.toFixed(0);
      },
      distributed: true,
      position: "top",
      anchor: "end",
      dropShadow: {
        enabled: false,
      },
    },
    colors: ["#8A2BE2", "#FFB323", "#377D51"],
    plotOptions: {
      bar: {
        columnWidth: 20,
        borderRadius: 2,
        dataLabels: {
          position: "top",
          maxItems: 100,
          hideOverflowingLabels: true,
        },
      },
    },
    xaxis: {
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      labels: {
        style: {
          colors: "#64748B",
          fontSize: "14px",
          fontFamily: "Satoshi",
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      title: {
        text: "Number of drops per user per month",
        style: {
          fontSize: "12px",
          fontWeight: "bold",
        },
      },
      labels: {
        style: {
          colors: "#64748B",
          fontSize: "14px",
          fontFamily: "Satoshi",
          fontWeight: 500,
        },
      },
    },
    grid: {
      show: true,
      borderColor: "#dddddd",
      strokeDashArray: 5,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: 5,
        right: 0,
      },
    },
    fill: {
      opacity: 1,
    },
  },
};
